import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import type { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import type { DeclarativeResidence } from "@/features/flow/types";
import { FlowHeader } from "@/flow/components/flow-header.react";
import { FormAddAddress } from "@/molecules/form-add-address/index.react";
import { useAppDispatch } from "@/redux/hooks";
import { setIsInPreviousStep } from "@/redux/slices/flowSlice";
import { isDeclarativeResidenceValid } from "@/utils/declarative.utils";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface DeclarativeIdentityResidenceProps extends LayoutBaseProps {
  onValidated?: (output: DeclarativeIdentityResidenceOutput) => void;
  declarativeResidence: DeclarativeResidence;
}

export type DeclarativeIdentityResidenceOutput = DeclarativeResidence;

function DeclarativeIdentityResidence({
  declarativeResidence,
  onValidated = () => {}
}: DeclarativeIdentityResidenceProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["pass", "atoms"]);
  const [data, setData] = useState<DeclarativeResidence>({
    addressLine: declarativeResidence?.addressLine ?? "",
    city: declarativeResidence?.city ?? "",
    countryCode: declarativeResidence?.countryCode ?? "",
    zip: declarativeResidence?.zip ?? ""
  });
  const [addressValid, setAddressValid] = useState(
    declarativeResidence?.addressLine &&
      declarativeResidence?.city &&
      declarativeResidence?.countryCode &&
      declarativeResidence?.zip
      ? true
      : false
  );

  function execute() {
    if (!isDeclarativeResidenceValid(data) || !addressValid) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }
    dispatch(setIsInPreviousStep(false));
    onValidated(data);
  }
  return (
    <>
      <Column className="justify-between">
        <div className="mb-12">
          <FlowHeader
            title={t("flow.id_control.signup.declarative_identity.label.three_title")}
            description={t("flow.id_control.signup.declarative_identity.label.three_description")}
            onPreviousClick={() => dispatch(setIsInPreviousStep(true))}
          />
          <FormAddAddress
            autoFocus
            residence={true}
            fields={{
              tag: { visible: false },
              isPrincipalAddress: { visible: false },
              address: { visible: true, label: t("flow.id_control.input.address_label") },
              country: {
                visible: true,
                required: true,
                label: t("flow.id_control.input.country_of_residence_label")
              },
              city: {
                visible: true,
                required: true,
                label: t("flow.id_control.input.city_of_residence_label")
              },
              zip: {
                visible: true,
                required: true,
                label: t("flow.id_control.input.zip_label")
              }
            }}
            address={{
              address: data.addressLine ?? "",
              zip: data.zip ?? "",
              country: parseInt(data.countryCode ?? "-1"),
              city: data.city ?? "",
              tag: t("atoms:molecules.add_address_form.is_principal_address"),
              isPrincipalAddress: true
            }}
            onChange={(address, valid) => {
              setAddressValid(valid);
              setData((data) => ({
                ...data,
                addressLine: address.address,
                city: address.city,
                zip: address.zip,
                countryCode: (address.country ?? "").toString()
              }));
            }}
            className={"mt-0 mb-0"}
          />
        </div>
        <div className="flex shrink-0 flex-col items-center">
          <DotsNavigation total={8} current={3} />
          <Button
            shortcut="enter"
            onClick={() => execute()}
            icon={<ChevronRightIcon />}
            className="mt-4"
          >
            {t("flow.id_control.input.continue")}
          </Button>
        </div>
      </Column>
    </>
  );
}

export default DeclarativeIdentityResidence;
